import React from 'react';

import { Input, useTranslation } from '@testsigma/atoms';

interface ScratchOrgFieldsProps {
  /** The watch function from react-hook-form used to retrieve the latest value from the form state */
  watch: (field: string) => any;
  /** Function to update the form field identified by fieldId with the value fieldValue*/
  handleUpdateFormValue: (fieldId: string, fieldValue: any) => void;
}

/**
 * Renders the UI for the fields (Instance URL and Expiration Date) related to Scratch Org connections.
 * @param props.watch The watch function from react-hook-form used to retrieve the latest value from the form state
 * @param props.handleUpdateFormValue Function to update the form field identified by fieldId with the value fieldValue
 *   - **Parameters:**
 *     - `fieldId` (string): The identifier of the form field to be updated.
 *     - `fieldValue` (any): The new value to set for the specified form field.
 *
 *   - **Returns:**
 *     - `void`: This function does not return a value; it updates the form field with the new value.
 *
 * @returns UI for fields associated with Scratch Org connections.
 */
const ScratchOrgFields: React.FC<ScratchOrgFieldsProps> = (
  props: ScratchOrgFieldsProps,
) => {
  const { handleUpdateFormValue, watch } = props;
  const { t } = useTranslation();

  return (
    <>
      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('instance-url.label')}
        </div>
        <Input
          isRequired={true}
          className='w-[320px] h-7'
          type='text'
          onChange={(value) => handleUpdateFormValue?.('instanceUrl', value)}
          hideErrorMessage={true}
          value={watch('instanceUrl')}
          placeholder='https://your-scratch-org.scratch.my.salesforce.com'
        />
      </div>

      <div className='flex flex-row py-2'>
        <div className='text-sm w-[200px] leading-5 text-secondary-500'>
          {t('expiration-date.label')}
        </div>
        <Input
          isRequired={true}
          className='w-[320px] h-7'
          type='date'
          onChange={(value) => handleUpdateFormValue?.('expirationDate', value)}
          hideErrorMessage={true}
          value={
            watch('expirationDate')
              ? new Date(watch('expirationDate')).toISOString().split('T')[0]
              : ''
          }
          placeholder={t('expiration-date.label')}
        />
      </div>
    </>
  );
};

export { ScratchOrgFields };
